// services/campaignApi.js
import api, { ensureCsrfCookie } from "../axiosConfig";

export const campaignApi = {
  /**
   * List campaigns (general / WhatsApp)
   * Supports filters and pagination.
   * Example: list({ page: 1, channel: "whatsapp", status: "active", q: "promo" })
   */
  list: (params = {}) => {
    const {
      page = 1,
      channel, // "whatsapp"
      status,  // "active|paused|scheduled|completed|failed"
      q,       // search string
      per_page // optional
    } = params;

    const query = new URLSearchParams();
    query.set("page", String(page));
    if (channel) query.set("channel", channel);
    if (status) query.set("status", status);
    if (q) query.set("q", q);
    if (per_page) query.set("per_page", String(per_page));

    return api.get(`/campaigns?${query.toString()}`);
  },

  /**
   * Create campaign (general / WhatsApp)
   */
  create: (data) => api.post("/campaigns", data),

  /**
   * Update campaign (EDIT) (general / WhatsApp)
   */
  update: (id, data) => api.put(`/campaigns/${id}`, data),

  /**
   * Delete campaign (general / WhatsApp)
   */
  remove: (id) => api.delete(`/campaigns/${id}`),

  /**
   * Get a single campaign (general / WhatsApp)
   */
  get: (id) => api.get(`/campaigns/${id}`),

  /**
   * Live stats for a campaign (general / WhatsApp)
   */
  stats: (id) => api.get(`/campaigns/${id}/stats`),

  /**
   * Campaign recipients / delivery details
   * GET /api/campaigns/{id}/recipients
   */
  recipients: (id, params = {}) => api.get(`/campaigns/${id}/recipients`, { params }),

  /**
   * Campaign activity timeline
   * GET /api/campaigns/{id}/history
   */
  history: (id, params = {}) => api.get(`/campaigns/${id}/history`, { params }),

  /**
   * Cross-campaign contact engagement insights
   * GET /api/campaigns/contact-insights
   */
  contactInsights: (params = {}) => api.get(`/campaigns/contact-insights`, { params }),

  /**
   * Global WhatsApp summary (KPIs)
   * GET /api/campaigns/summary
   */
  summary: () => api.get(`/campaigns/summary`),

  /**
   * ✅ Campaign performance (prevents "performance is not a function")
   * GET /api/campaigns/performance?scope=all&period=week
   */
  performance: (params = {}) => {
    const { scope = "all", period = "week" } = params;

    const query = new URLSearchParams();
    query.set("scope", String(scope));
    query.set("period", String(period));

    return api.get(`/campaigns/performance?${query.toString()}`);
  },

  /**
   * Calendar feed (general / WhatsApp)
   * GET /campaigns/calendar?start=YYYY-MM-DD&end=YYYY-MM-DD
   */
  calendar: (start, end) =>
    api.get(
      `/campaigns/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
    ),

  // Backwards compat alias
  getCalendar: (start, end) =>
    api.get(
      `/campaigns/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
    ),

  /**
   * Pause / Resume (general / WhatsApp)
   * POST /api/campaigns/{id}/pause
   * POST /api/campaigns/{id}/resume
   */
  pause: (id) => api.post(`/campaigns/${id}/pause`),
  resume: (id) => api.post(`/campaigns/${id}/resume`),

  /**
   * Optional: bulk or source-based sync
   */
  sync: (source) => api.post("/marketing/sync", source ? { source } : {}),

  /**
   * Resolve recipients (WhatsApp only)
   * POST /api/whatsapp/campaigns/{campaign}/resolve-recipients
   */
  resolveRecipients: (campaignId, payload) =>
    api.post(`/whatsapp/campaigns/${campaignId}/resolve-recipients`, payload),

  uploadTemplateMedia: async (file, format) => {
    await ensureCsrfCookie();

    const formData = new FormData();
    formData.append("file", file);
    formData.append("format", String(format || "").toUpperCase());

    return api.post("/whatsapp/templates/media", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // ---------------------------------------------------------------------------
  // ✅ EMAIL CAMPAIGNS (Scheduling support)
  // These match the endpoints you’re already using in emailDashboardApi:
  // - GET  /email/dashboard
  // - GET  /email/campaigns/calendar
  // - PATCH /email/campaigns/{id}/schedule
  // ---------------------------------------------------------------------------

  /**
   * Email dashboard (KPIs + funnel + calendar items)
   * GET /email/dashboard?days=30
   */
  emailDashboard: async (days = 30) => {
    await ensureCsrfCookie();
    return api.get("/email/dashboard", { params: { days } });
  },

  /**
   * Email campaigns calendar feed
   * GET /email/campaigns/calendar?start=...&end=... OR other params
   */
  emailCalendar: async (params = {}) => {
    await ensureCsrfCookie();
    return api.get("/email/campaigns/calendar", { params });
  },

  /**
   * Create Email campaign (supports scheduling)
   * POST /email/campaigns
   *
   * Expected payload fields (example):
   * {
   *   subject, body_html, recipient_ids: [...],
   *   scheduled_at: null | ISOString,
   *   ...other composer fields
   * }
   */
  createEmailCampaign: async (payload) => {
    await ensureCsrfCookie();
    return api.post("/email/campaigns", payload);
  },

  /**
   * Update schedule for an Email campaign
   * PATCH /email/campaigns/{id}/schedule
   *
   * payload example:
   * { scheduled_at: "2026-03-03T10:30:00.000Z" }
   */
  updateEmailSchedule: async (campaignId, payload) => {
    await ensureCsrfCookie();
    return api.patch(`/email/campaigns/${campaignId}/schedule`, payload);
  },

  /**
   * Queue now (send immediately) for an Email campaign
   * If your backend uses a dedicated endpoint, change the URL here.
   * Otherwise you can implement "queue now" as schedule = now.
   */
  queueEmailNow: async (campaignId) => {
    await ensureCsrfCookie();
    return api.post(`/email/campaigns/${campaignId}/queue-now`);
  },
};
